import { context, propagation } from '@opentelemetry/api';
import type { IFrameTransportService } from '@/domain/interfaces/i-frame-transport-service';
import { FilterData, GrayscaleAlgorithm, ImageData } from '@/domain/value-objects';
import {
  DataChannelRequest,
  DetectionFrame,
  GenericFilterParameterSelection,
  GenericFilterSelection,
  ProcessingStatsFrame,
  ProcessImageRequest,
  ProcessImageResponse,
} from '@/gen/image_processor_service_pb';
import {
  AcceleratorType,
  TraceContext,
} from '@/gen/common_pb';
import type { WebRTCSessionMode } from '@/domain/value-objects/webrtc-session';

import { ChunkReassembler, nextMessageId, packMessage } from './data-channel-framing';
import { webrtcService } from '@/infrastructure/connection/webrtc-service';
import { logger } from '@/infrastructure/observability/otel-logger';
import { markStart, markEnd } from '@/infrastructure/observability/perf-mark';

type FrameResultCallback = (data: ProcessImageResponse) => void;

const REQUEST_TIMEOUT_MS = 10_000;

type PendingResponse = {
  resolve: (response: ProcessImageResponse) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

function serializeValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

function toGenericFilterSelections(filters: FilterData[]): GenericFilterSelection[] {
  return filters
    .map((filter) => {
      const parameters = Object.entries(filter.getParameters())
        .map(([parameterId, value]) => {
          const values = Array.isArray(value)
            ? value.map((entry) => serializeValue(entry)).filter((entry) => entry !== '')
            : [serializeValue(value)].filter((entry) => entry !== '');
          if (values.length === 0) {
            return null;
          }
          return new GenericFilterParameterSelection({
            parameterId,
            values,
          });
        })
        .filter((selection): selection is GenericFilterParameterSelection => selection !== null);

      return new GenericFilterSelection({
        filterId: filter.getId(),
        parameters,
      });
    });
}

function buildTraceContext(): TraceContext {
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier);
  return new TraceContext({
    traceparent: carrier.traceparent ?? '',
    tracestate: carrier.tracestate ?? '',
  });
}

async function rasterizeImageData(
  dataUrl: string,
  width: number,
  height: number
): Promise<{ imageData: Uint8Array; channels: number }> {
  const image = new Image();
  image.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Failed to load frame image data'));
    image.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Canvas context is not available');
  }

  context.drawImage(image, 0, 0, width, height);
  const raster = context.getImageData(0, 0, width, height);
  const rgb = new Uint8Array(width * height * 3);

  for (let index = 0, pixel = 0; index < raster.data.length; index += 4, pixel += 3) {
    rgb[pixel] = raster.data[index];
    rgb[pixel + 1] = raster.data[index + 1];
    rgb[pixel + 2] = raster.data[index + 2];
  }

  return {
    imageData: rgb,
    channels: 3,
  };
}

export class WebRTCFrameTransportService implements IFrameTransportService {
  private sessionId: string | null = null;
  private frameResultCallback: FrameResultCallback | null = null;
  private detectionResultCallback: ((frame: DetectionFrame) => void) | null = null;
  private statsResultCallback: ((frame: ProcessingStatsFrame) => void) | null = null;
  private frameIdCounter = 0n;
  private connectPromise: Promise<void> | null = null;
  private pendingResponses: Map<bigint, PendingResponse> = new Map();
  private connectionState: 'connected' | 'disconnected' | 'connecting' | 'error' = 'disconnected';
  private frameChannelReassembler = new ChunkReassembler();
  private detectionChannelReassembler = new ChunkReassembler();
  private statsChannelReassembler = new ChunkReassembler();
  private lastRequest: string | null = null;
  private lastRequestTime: Date | null = null;

  constructor(
    private sourceId: string,
    private statsManager: IStatsDisplay,
    private cameraManager: ICameraPreview,
    private toastManager: IToastDisplay,
    private sessionMode: WebRTCSessionMode = 'frame-processing',
    private onRemoteStream?: (stream: MediaStream) => void
  ) {}

  connect(): void {
    void this.ensureConnected();
  }

  disconnect(): void {
    const sessionId = this.sessionId;
    this.sessionId = null;
    this.connectPromise = null;
    this.connectionState = 'disconnected';
    const pending = Array.from(this.pendingResponses.values());
    this.pendingResponses.clear();
    for (const entry of pending) {
      clearTimeout(entry.timer);
      entry.reject(new Error('Transport disconnected'));
    }
    this.statsManager.updateTransportStatus('disconnected', 'Disconnected');

    if (sessionId) {
      void webrtcService.closeSession(sessionId).catch((error) => {
        logger.error('Failed to close frame transport session', {
          'error.message': error instanceof Error ? error.message : String(error),
          'webrtc.session_id': sessionId,
        });
      });
    }
  }

  sendFrame(
    imageData: string,
    width: number,
    height: number,
    filters: FilterData[],
    accelerator: AcceleratorType
  ): void {
    this.sendFrameWithImageData(new ImageData(imageData, width, height), filters, accelerator);
  }

  sendFrameWithImageData(image: ImageData, filters: FilterData[], accelerator: AcceleratorType): void {
    this.sendFrameWithValueObjects(image, filters, accelerator);
  }

  sendFrameWithValueObjects(image: ImageData, filters: FilterData[], accelerator: AcceleratorType): void {
    this.sendFrameWithProcessingConfig(
      image,
      filters,
      accelerator,
      new GrayscaleAlgorithm('bt601')
    );
  }

  sendFrameWithProcessingConfig(
    image: ImageData,
    filters: FilterData[],
    accelerator: AcceleratorType,
    grayscale: GrayscaleAlgorithm
  ): void {
    void grayscale;
    void this.sendRequest(image, filters, accelerator, grayscale, { awaitResponse: false }).catch((error) => {
      this.handleError('Failed to send frame', error);
    });
  }

  sendSingleFrame(
    imageData: string,
    width: number,
    height: number,
    filters: FilterData[],
    accelerator: AcceleratorType
  ): Promise<ProcessImageResponse> {
    const image = new ImageData(imageData, width, height);
    const grayscale = new GrayscaleAlgorithm('bt601');

    return this.sendRequest(image, filters, accelerator, grayscale, { awaitResponse: true }) as Promise<ProcessImageResponse>;
  }

  async sendSingleImage(
    rasterizedBytes: Uint8Array,
    width: number,
    height: number,
    channels: number,
    filters: FilterData[],
    accelerator: AcceleratorType,
    grayscale: GrayscaleAlgorithm
  ): Promise<ProcessImageResponse> {
    await this.ensureConnected();
    if (!this.sessionId) {
      throw new Error('WebRTC session is not available');
    }

    const dataChannel = webrtcService.getDataChannel(this.sessionId);
    if (!dataChannel || dataChannel.readyState !== 'open') {
      throw new Error('WebRTC data channel is not open');
    }

    this.lastRequest = 'ProcessImageRequest';
    this.lastRequestTime = new Date();

    const frameId = ++this.frameIdCounter;
    const pending = this.registerPending(frameId);
    const requestImageData = new Uint8Array(rasterizedBytes);

    const payload = new DataChannelRequest({
      payload: {
        case: 'processImage',
        value: new ProcessImageRequest({
      imageData: requestImageData,
      width,
      height,
      channels,
      filters: [],
      accelerator,
      genericFilters: toGenericFilterSelections(filters),
      sessionId: this.sessionId,
      traceContext: buildTraceContext(),
      apiVersion: '1.1',
      frameId,
        }),
      },
    }).toBinary();

    try {
      for (const chunk of packMessage(nextMessageId(), payload)) {
        dataChannel.send(chunk);
      }
    } catch (error) {
      this.rejectPending(frameId, error instanceof Error ? error : new Error(String(error)));
      throw error;
    }

    return pending;
  }

  onFrameResult(callback: (data: ProcessImageResponse) => void): void {
    this.frameResultCallback = callback;
  }

  onDetectionResult(callback: (frame: DetectionFrame) => void): void {
    this.detectionResultCallback = callback;
  }

  onStatsResult(callback: (frame: ProcessingStatsFrame) => void): void {
    this.statsResultCallback = callback;
  }

  isConnected(): boolean {
    if (!this.sessionId) {
      return false;
    }
    return webrtcService.getDataChannel(this.sessionId)?.readyState === 'open';
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getConnectionStatus(): { state: 'connected' | 'disconnected' | 'connecting' | 'error'; lastRequest: string | null; lastRequestTime: Date | null } {
    return {
      state: this.connectionState,
      lastRequest: this.lastRequest,
      lastRequestTime: this.lastRequestTime,
    };
  }

  private async ensureConnected(): Promise<void> {
    if (this.isConnected()) {
      return;
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectionState = 'connecting';
    this.statsManager.updateTransportStatus('connecting', 'Connecting...');

    this.connectPromise = (async () => {
      const ensureMark = markStart('transport.ensure-connected');
      const session = await webrtcService.createSession(this.sourceId, {
        mode: this.sessionMode,
        onRemoteStream: this.onRemoteStream,
      });
      this.sessionId = session.getId();
      // waitForTransportReady waits until readyState === 'open' AND the
      // underlying SCTP association is 'connected'. Without the second wait
      // the first send() races the SCTP handshake and Chrome tears the
      // channel down with OperationError: data-channel-failure.
      const dataChannel = await webrtcService.waitForTransportReady(session.getId());
      webrtcService.ensureStatsDataChannel(session.getId());
      this.attachDataChannel(dataChannel);
      this.attachDetectionChannel(session.getId());
      this.attachStatsChannel(session.getId());
      this.connectionState = 'connected';
      this.lastRequest = 'Data channel connected';
      this.lastRequestTime = new Date();
      this.statsManager.updateTransportStatus('connected', 'Connected');
      markEnd('transport.ensure-connected', ensureMark);
    })().finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  private attachDataChannel(dataChannel: RTCDataChannel): void {
    dataChannel.binaryType = 'arraybuffer';
    this.frameChannelReassembler = new ChunkReassembler();
    const previousOnMessage = dataChannel.onmessage;
    const previousOnError = dataChannel.onerror;
    const previousOnClose = dataChannel.onclose;

    dataChannel.onmessage = (event: MessageEvent<ArrayBuffer | Blob | string>) => {
      previousOnMessage?.call(dataChannel, event);
      const payload = event.data;
      if (typeof payload === 'string') {
        return;
      }
      const bufferPromise = payload instanceof Blob ? payload.arrayBuffer() : Promise.resolve(payload);
      void bufferPromise.then((buffer) => {
        const assembled = this.frameChannelReassembler.pushChunk(buffer);
        if (assembled === null) {
          return;
        }
        this.handleResponse(ProcessImageResponse.fromBinary(assembled));
      }).catch((error) => {
        this.handleError('Failed to decode frame response', error);
      });
    };
    dataChannel.onerror = (event) => {
      previousOnError?.call(dataChannel, event);
      this.connectionState = 'error';
      this.statsManager.updateTransportStatus('disconnected', 'Transport error');
    };
    dataChannel.onclose = (event: Event) => {
      previousOnClose?.call(dataChannel, event);
      this.connectionState = 'disconnected';
      this.statsManager.updateTransportStatus('disconnected', 'Disconnected');
      this.failPendingResponses(new Error('WebRTC data channel closed'));
    };
  }

  private failPendingResponses(error: Error): void {
    if (this.pendingResponses.size === 0) {
      return;
    }
    const pending = Array.from(this.pendingResponses.entries());
    this.pendingResponses.clear();
    for (const [, entry] of pending) {
      clearTimeout(entry.timer);
      entry.reject(error);
    }
  }

  private attachDetectionChannel(sessionId: string): void {
    const dc = webrtcService.getDetectionDataChannel(sessionId);
    if (!dc) return;
    this.detectionChannelReassembler = new ChunkReassembler();
    const previousOnMessage = dc.onmessage;
    dc.onmessage = (event: MessageEvent) => {
      previousOnMessage?.call(dc, event);
      const payload = event.data;
      const bufferPromise = payload instanceof Blob ? payload.arrayBuffer() : Promise.resolve(payload as ArrayBuffer);
      void bufferPromise.then((buffer) => {
        const assembled = this.detectionChannelReassembler.pushChunk(buffer);
        if (assembled === null) {
          return;
        }
        const frame = DetectionFrame.fromBinary(assembled);
        this.detectionResultCallback?.(frame);
      }).catch((error) => {
        this.handleError('Failed to decode detection frame', error);
      });
    };
  }

  private attachStatsChannel(sessionId: string): void {
    const dc = webrtcService.getStatsDataChannel(sessionId);
    if (!dc) return;
    this.statsChannelReassembler = new ChunkReassembler();
    dc.onmessage = (event: MessageEvent) => {
      const payload = event.data;
      const bufferPromise = payload instanceof Blob ? payload.arrayBuffer() : Promise.resolve(payload as ArrayBuffer);
      void bufferPromise.then((buffer) => {
        const assembled = this.statsChannelReassembler.pushChunk(buffer);
        if (assembled === null) {
          return;
        }
        const frame = ProcessingStatsFrame.fromBinary(assembled);
        this.statsResultCallback?.(frame);
      }).catch((error) => {
        this.handleError('Failed to decode processing stats frame', error);
      });
    };
  }

  private async sendRequest(
    image: ImageData,
    filters: FilterData[],
    accelerator: AcceleratorType,
    grayscale: GrayscaleAlgorithm,
    options: { awaitResponse: boolean }
  ): Promise<ProcessImageResponse | void> {
    await this.ensureConnected();
    if (!this.sessionId) {
      throw new Error('WebRTC session is not available');
    }

    const dataChannel = webrtcService.getDataChannel(this.sessionId);
    if (!dataChannel || dataChannel.readyState !== 'open') {
      throw new Error('WebRTC data channel is not open');
    }

    this.cameraManager.setProcessing(true);
    this.lastRequest = 'ProcessImageRequest';
    this.lastRequestTime = new Date();

    const rasterized = await rasterizeImageData(
      image.getBase64(),
      image.getWidth(),
      image.getHeight()
    );

    const frameId = ++this.frameIdCounter;
    const pending = options.awaitResponse ? this.registerPending(frameId) : null;

    void grayscale;

    const payload = new DataChannelRequest({
      payload: {
        case: 'processImage',
        value: new ProcessImageRequest({
      imageData: new Uint8Array(rasterized.imageData),
      width: image.getWidth(),
      height: image.getHeight(),
      channels: rasterized.channels,
      filters: [],
      accelerator,
      genericFilters: toGenericFilterSelections(filters),
      sessionId: this.sessionId,
      traceContext: buildTraceContext(),
      apiVersion: '1.1',
      frameId,
        }),
      },
    }).toBinary();

    try {
      for (const chunk of packMessage(nextMessageId(), payload)) {
        dataChannel.send(chunk);
      }
    } catch (error) {
      if (pending) {
        this.rejectPending(frameId, error instanceof Error ? error : new Error(String(error)));
      }
      throw error;
    }

    if (pending) {
      return pending;
    }
    return undefined;
  }

  private registerPending(frameId: bigint): Promise<ProcessImageResponse> {
    return new Promise<ProcessImageResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingResponses.delete(frameId);
        reject(new Error(`Frame ${frameId} timed out after ${REQUEST_TIMEOUT_MS}ms`));
      }, REQUEST_TIMEOUT_MS);
      this.pendingResponses.set(frameId, { resolve, reject, timer });
    });
  }

  private rejectPending(frameId: bigint, error: Error): void {
    const entry = this.pendingResponses.get(frameId);
    if (!entry) {
      return;
    }
    clearTimeout(entry.timer);
    this.pendingResponses.delete(frameId);
    entry.reject(error);
  }

  private handleResponse(response: ProcessImageResponse): void {
    this.cameraManager.setProcessing(false);
    this.connectionState = 'connected';
    this.lastRequest = 'ProcessImageResponse';
    this.lastRequestTime = new Date();

    if (response.code === 0) {
      this.statsManager.updateProcessingStats(performance.now() - this.cameraManager.getLastFrameTime());
    } else {
      this.toastManager.error('Processing Error', response.message || 'Unknown error');
      this.statsManager.updateCameraStatus('Processing failed', 'error');
    }

    const frameId = response.frameId;
    if (frameId && frameId !== 0n) {
      const pending = this.pendingResponses.get(frameId);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingResponses.delete(frameId);
        if (response.code === 0) {
          pending.resolve(response);
        } else {
          pending.reject(new Error(response.message || 'Frame processing failed'));
        }
      }
    }

    this.frameResultCallback?.(response);

    if (this.detectionResultCallback && response.detections && response.detections.length > 0) {
      const synthesized = new DetectionFrame({
        frameId: response.frameId,
        imageWidth: response.width,
        imageHeight: response.height,
        detections: response.detections,
      });
      this.detectionResultCallback(synthesized);
    }
  }

  private handleError(message: string, error: unknown): void {
    const normalized = error instanceof Error ? error : new Error(String(error));
    this.connectionState = 'error';
    this.cameraManager.setProcessing(false);
    this.statsManager.updateTransportStatus('disconnected', 'Connection error');
    logger.error(message, {
      'error.message': normalized.message,
      'webrtc.source_id': this.sourceId,
    });
    this.toastManager.warning('Transport Error', normalized.message);
  }
}
